import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  sendVerificationEmail,
  initiatePasswordReset,
  changeEmailAddress,
  isEmailVerified,
  reauthenticateUser,
} from '../../services/authService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import NewCustomButton from '../common/NewCustomButton';
import { appTypographySx, getCommonStyles, getAppTextPrimary } from '../../theme';
import {
  Mail as MailIcon,
  Shield,
  CheckCircle,
  AlertCircle,
  Lock,
} from 'lucide-react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Alert,
  Skeleton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

const LoadingSkeleton = ({ sectionPaperSx }) => (
  <Stack spacing={2}>
    {[1, 2, 3].map((i) => (
      <Paper key={i} elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, ...sectionPaperSx }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Skeleton height={22} width="40%" sx={{ mb: 1 }} />
            <Skeleton height={16} width="55%" />
          </Box>
          <Skeleton height={36} width={120} sx={{ borderRadius: '999px' }} />
        </Stack>
      </Paper>
    ))}
  </Stack>
);

const SecurityActionRow = ({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  description,
  action,
  isDarkMode,
  sectionPaperSx,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
      ...sectionPaperSx,
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: iconBg,
          }}
        >
          <Icon size={18} style={{ color: iconColor }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}
          >
            {description}
          </Typography>
        </Box>
      </Stack>
      <Box sx={{ flexShrink: 0 }}>{action}</Box>
    </Stack>
  </Paper>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('AccountManagement component error:', error, errorInfo);
    this.setState({ error });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          className="account-management-page"
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            p: 0,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
              Account Management
            </Typography>
            <Typography variant="body2">
              Manage your account security and email settings
            </Typography>
          </Box>
          <Alert severity="error" sx={{ borderRadius: '16px' }}>
            {this.state.error?.toString() || 'Something went wrong loading this page.'}
          </Alert>
          <NewCustomButton onClick={() => window.location.reload()}>Try again</NewCustomButton>
        </Box>
      );
    }

    return this.props.children;
  }
}

const AccountManagement = () => {
  const { currentUser, refreshUser } = useAuth();
  const { isDarkMode, primaryColor } = useTheme();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [isEmailChangeDialogOpen, setIsEmailChangeDialogOpen] = useState(false);
  const [functionsAvailable, setFunctionsAvailable] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();

  const commonStyles = getCommonStyles(isDarkMode);
  const pageTextPrimary = getAppTextPrimary(isDarkMode);
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const sectionPaperSx = {
    borderRadius: '16px',
    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
    border: '1px solid',
    borderColor,
    ...commonStyles,
  };
  const controlSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#fff',
      fontSize: '0.875rem',
      '& fieldset': { borderColor },
      '&.Mui-focused fieldset': { borderColor: primaryColor },
    },
    '& .MuiInputLabel-root': { color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'text.primary' },
  };
  const dialogPaperSx = {
    borderRadius: '16px',
    bgcolor: isDarkMode ? '#1a1a1a' : '#fff',
    color: isDarkMode ? '#fff' : 'inherit',
    border: '1px solid',
    borderColor,
    ...commonStyles,
  };
  const pageShellSx = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    color: pageTextPrimary,
    ...appTypographySx(isDarkMode),
  };

  const emailVerified = isEmailVerified();

  useEffect(() => {
    const checkFunctions = async () => {
      try {
        setInitialLoading(true);

        if (!functions) {
          setFunctionsAvailable(false);
          setMessage({
            type: 'error',
            text: 'Firebase Functions could not be initialized. Please try again later.',
          });
          return;
        }

        try {
          httpsCallable(functions, 'sendPasswordReset');
          setFunctionsAvailable(true);
        } catch (callableError) {
          console.error('Firebase Functions callable creation failed:', callableError);
          setFunctionsAvailable(false);
          setMessage({
            type: 'error',
            text: 'Email functions are currently unavailable. Please try again later.',
          });
        }
      } catch (error) {
        console.error('Firebase Functions check failed:', error);
        setFunctionsAvailable(false);
        setMessage({
          type: 'error',
          text: 'Email functions are currently unavailable due to a system error. Please try again later.',
        });
      } finally {
        setInitialLoading(false);
      }
    };

    checkFunctions();
  }, []);

  const handleSendVerificationEmail = async () => {
    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await sendVerificationEmail();
      setMessage({
        type: 'success',
        text: 'Verification email sent! Check your inbox and spam folder.',
      });
    } catch (error) {
      console.error('Error sending verification email:', error);
      setMessage({
        type: 'error',
        text: `Failed to send verification email: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.',
      });
      return;
    }

    if (!currentUser?.email) {
      setMessage({
        type: 'error',
        text: 'No email associated with your account.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await initiatePasswordReset(currentUser.email);
      setMessage({
        type: 'success',
        text: 'Password reset email sent! Check your inbox and spam folder.',
      });
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setMessage({
        type: 'error',
        text: `Failed to send password reset email: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();

    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.',
      });
      return;
    }

    if (!newEmail || !currentPassword) {
      setMessage({
        type: 'error',
        text: 'Please fill in all fields.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await reauthenticateUser(currentPassword);
      await changeEmailAddress(newEmail);

      setMessage({
        type: 'success',
        text: 'Email change initiated! Check your new email for verification.',
      });

      setNewEmail('');
      setCurrentPassword('');
      setIsEmailChangeDialogOpen(false);
      await refreshUser();
    } catch (error) {
      console.error('Error changing email:', error);
      if (error.code === 'auth/wrong-password') {
        setMessage({ type: 'error', text: 'Current password is incorrect.' });
      } else if (error.code === 'auth/email-already-in-use') {
        setMessage({ type: 'error', text: 'This email is already in use by another account.' });
      } else if (error.code === 'auth/invalid-email') {
        setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      } else {
        setMessage({
          type: 'error',
          text: `Failed to initiate email change: ${error.message || 'Unknown error'}`,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const closeEmailDialog = () => {
    setIsEmailChangeDialogOpen(false);
    setNewEmail('');
    setCurrentPassword('');
  };

  if (initialLoading) {
    return (
      <Box className="account-management-page" sx={pageShellSx}>
        <Box>
          <Skeleton variant="text" width={280} height={36} />
          <Skeleton variant="text" width={360} height={24} sx={{ mt: 0.5 }} />
        </Box>
        <LoadingSkeleton sectionPaperSx={sectionPaperSx} />
      </Box>
    );
  }

  return (
    <Box className="account-management-page" sx={pageShellSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>
            Account Management
          </Typography>
          <Typography variant="body2">
            Manage your account security and email settings
            {currentUser?.email ? ` for ${currentUser.email}` : ''}
          </Typography>
        </Box>
        <NewCustomButton variant="outlined" onClick={() => navigate('/profile')}>
          Back to profile
        </NewCustomButton>
      </Box>

      {message.text && (
        <Alert
          severity={message.type === 'success' ? 'success' : 'error'}
          onClose={() => setMessage({ type: '', text: '' })}
          sx={{ borderRadius: '16px' }}
        >
          {message.text}
        </Alert>
      )}

      {!functionsAvailable && (
        <Alert severity="warning" sx={{ borderRadius: '16px' }}>
          Email functions are currently unavailable. Some features may not work properly.
        </Alert>
      )}

      <Paper elevation={0} sx={{ overflow: 'hidden', ...sectionPaperSx }}>
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            borderBottom: '1px solid',
            borderColor,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${primaryColor}18`,
              }}
            >
              <Shield size={20} style={{ color: primaryColor }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Account Security
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}
              >
                Manage your email verification and password settings
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Stack spacing={2} sx={{ p: { xs: 2, sm: 3 } }}>
          <SecurityActionRow
            icon={emailVerified ? CheckCircle : MailIcon}
            iconBg={
              emailVerified
                ? isDarkMode
                  ? 'rgba(16, 185, 129, 0.2)'
                  : 'rgba(16, 185, 129, 0.12)'
                : isDarkMode
                  ? 'rgba(245, 158, 11, 0.2)'
                  : 'rgba(245, 158, 11, 0.12)'
            }
            iconColor={emailVerified ? '#10B981' : '#F59E0B'}
            title="Email Verification"
            description={
              emailVerified
                ? 'Your email is verified'
                : 'Verify your email to access all features'
            }
            isDarkMode={isDarkMode}
            sectionPaperSx={sectionPaperSx}
            action={
              emailVerified ? (
                <Chip
                  label="Verified"
                  size="small"
                  icon={<CheckCircle size={14} />}
                  sx={{
                    height: 28,
                    fontWeight: 600,
                    bgcolor: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.12)',
                    color: '#10B981',
                  }}
                />
              ) : (
                <NewCustomButton
                  onClick={handleSendVerificationEmail}
                  disabled={loading || !functionsAvailable}
                  compact
                >
                  {loading ? 'Sending…' : 'Verify email'}
                </NewCustomButton>
              )
            }
          />

          <SecurityActionRow
            icon={MailIcon}
            iconBg={isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.12)'}
            iconColor="#3B82F6"
            title="Password Reset"
            description="Reset your password if you've forgotten it or want to change it"
            isDarkMode={isDarkMode}
            sectionPaperSx={sectionPaperSx}
            action={
              <NewCustomButton
                onClick={handlePasswordReset}
                disabled={loading || !functionsAvailable}
                compact
              >
                {loading ? 'Sending…' : 'Reset password'}
              </NewCustomButton>
            }
          />

          <SecurityActionRow
            icon={MailIcon}
            iconBg={isDarkMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.12)'}
            iconColor="#8B5CF6"
            title="Change Email"
            description="Update your email address. You'll need to verify the new email"
            isDarkMode={isDarkMode}
            sectionPaperSx={sectionPaperSx}
            action={
              <NewCustomButton
                variant="outlined"
                onClick={() => setIsEmailChangeDialogOpen(true)}
                disabled={!functionsAvailable}
                compact
              >
                Change email
              </NewCustomButton>
            }
          />
        </Stack>
      </Paper>

      <Dialog
        open={isEmailChangeDialogOpen}
        onClose={closeEmailDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid', borderColor, pb: 2 }}>
          Change email address
        </DialogTitle>
        <Box component="form" onSubmit={handleEmailChange}>
          <DialogContent dividers sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'text.secondary' }}>
              Enter your new email address and current password to confirm the change.
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                id="newEmail"
                label="New email address"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                required
                sx={controlSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailIcon size={16} style={{ opacity: 0.45 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                id="currentPassword"
                label="Current password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                sx={controlSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock size={16} style={{ opacity: 0.45 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <NewCustomButton variant="secondary" type="button" onClick={closeEmailDialog}>
              Cancel
            </NewCustomButton>
            <NewCustomButton
              type="submit"
              disabled={loading || !newEmail || !currentPassword || !functionsAvailable}
            >
              {loading ? 'Processing…' : 'Confirm change'}
            </NewCustomButton>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

const AccountManagementWithErrorBoundary = () => (
  <ErrorBoundary>
    <AccountManagement />
  </ErrorBoundary>
);

export default AccountManagementWithErrorBoundary;
